import { useEffect } from 'react';
import { useProductSkus } from '../lib/product-skus-api';

/**
 * Dropdown SKU per riga MSS/MSF, basato sul productId derivato dal lotto selezionato.
 * Mostra fino a 5 SKU (5/10/20/50/100g) pre-popolati dal catalogo `ProductSku`.
 * L'operatore puo selezionare lo SKU corrispondente alla grammatura principale della riga.
 *
 * Se il lot e' selezionato MA non esistono SKU per quel product (es. MICROFILTRATO/TMC_OIL),
 * il dropdown e' vuoto + mostra placeholder "no SKU".
 */
export function ProductSkuPicker({
  productId,
  value,
  onChange,
  disabled,
  dominantGrammatura,
}: {
  productId: string | null;
  value: string;
  onChange: (sku: string) => void;
  disabled?: boolean;
  // Se passato, auto-seleziona lo SKU della grammatura indicata quando value e' vuoto.
  // Usato per auto-resolve dopo che l'operatore digita qty per una grammatura.
  dominantGrammatura?: number | null;
}) {
  const skusQ = useProductSkus(
    { productId: productId ?? undefined, active: true },
    !!productId,
  );

  // Auto-resolve: quando value e' vuoto, productId presente, e c'e' una dominantGrammatura,
  // imposta automaticamente lo SKU corrispondente.
  useEffect(() => {
    const items = skusQ.data?.items ?? [];
    if (value || !productId || !dominantGrammatura || items.length === 0) return;
    const match = items.find((it) => it.grammatura === dominantGrammatura);
    if (match) onChange(match.sku);
  }, [value, productId, dominantGrammatura, skusQ.data, onChange]);

  const items = skusQ.data?.items ?? [];

  if (!productId) {
    return (
      <input
        type="text"
        className="w-24 rounded border border-slate-300 bg-slate-50 px-1 py-1 text-xs text-slate-400"
        value=""
        placeholder="— lotto —"
        disabled
      />
    );
  }

  if (items.length === 0 && !skusQ.isLoading) {
    // Fallback per product senza SKU catalogati (es. MICROFILTRATO, oli)
    return (
      <input
        type="text"
        className="w-24 rounded border border-slate-300 px-1 py-1 text-xs uppercase"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="SKU libero"
      />
    );
  }

  return (
    <select
      className="w-24 rounded border border-slate-300 bg-white px-1 py-1 text-xs uppercase"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || skusQ.isLoading}
    >
      <option value="">— SKU —</option>
      {items.map((it) => (
        <option key={it.id} value={it.sku}>
          {it.sku} ({it.grammatura}g)
        </option>
      ))}
    </select>
  );
}
